// /sports/super-admin/sales — what the fleet took, from the mirrored rows
// (K6 Phase 2). Period + group toggle (device / site / tenant), totals,
// grouped table. Customers-paid is the phone's own figure; owner and court
// shares are DERIVED on the server with the phone's split rule and labelled.
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { BarChart3, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { SuperAdminShell } from './SportsSuperAdminPage';
import { getSales, isAdminDenied, errMsg, ymd, shiftYmd } from '../kiosk/kioskApi';
import { RED, GREEN, GREY, AMBER, AdminDeniedNotice, fmtCentavos, labelStyle, inputStyle } from '../kiosk/KioskDialogs';
import { useAutoRefresh } from '../kiosk/useAutoRefresh';
import { fmtHrMin } from '../liveUi';

const GROUPS = [['device', 'By device'], ['site', 'By site'], ['tenant', 'By tenant']];
const PRESETS = [['today', 'Today'], ['7', 'Last 7 days'], ['30', 'Last 30 days'], ['month', 'This month']];
// K8: selections by settlement state / everything — the server ignores from/to for these.
const ROLLING_PRESETS = [['since_paid', 'Since last paid'], ['all', 'All time']];
const card = { background: '#141414', border: '1px solid #1E1E1E', borderRadius: 12, padding: '14px 16px' };
const hours = (m) => `${((m || 0) / 60).toFixed(1)} h`;

function presetRange(key) {
  const today = ymd();
  if (key === 'today') return [today, today];
  if (key === 'month') return [`${today.slice(0, 7)}-01`, today];
  return [shiftYmd(today, -(Number(key) - 1)), today];
}

// `rolling` + `onRolling(kind|null)`: pages that can select by settlement state
// (Sales) show the two extra chips; picking a date range clears the rolling kind.
export function PeriodPicker({ from, to, onChange, rolling = null, onRolling = null, testPrefix = 'kiosk-period' }) {
  const chip = (k, label, on, onClick) => (
    <button key={k} onClick={onClick} data-testid={`${testPrefix}-preset-${k}`}
      style={{ fontSize: 12, fontWeight: 700, padding: '6px 12px', borderRadius: 999, border: `1px solid ${on ? RED : '#2A2A2A'}`,
        background: on ? RED + '18' : 'transparent', color: on ? RED : GREY, cursor: 'pointer' }}>
      {label}
    </button>
  );
  return (
    <div className="flex items-center gap-2 flex-wrap">
      {onRolling && ROLLING_PRESETS.map(([k, label]) => chip(k, label, rolling === k, () => onRolling(k)))}
      {PRESETS.map(([k, label]) => chip(k, label, false, () => { onRolling?.(null); onChange(...presetRange(k)); }))}
      <input type="date" value={from} max={to} onChange={e => e.target.value && onChange(e.target.value, to)} data-testid={`${testPrefix}-from`}
        style={{ ...inputStyle, width: 150, padding: '6px 8px', fontSize: 12 }} />
      <span style={{ color: '#555', fontSize: 12 }}>to</span>
      <input type="date" value={to} min={from} max={ymd()} onChange={e => e.target.value && onChange(from, e.target.value)} data-testid={`${testPrefix}-to`}
        style={{ ...inputStyle, width: 150, padding: '6px 8px', fontSize: 12 }} />
    </div>
  );
}

function Tile({ label, value, color = '#fff', sub, testId }) {
  return (
    <div style={card}>
      <p style={{ fontSize: 11, color: GREY, textTransform: 'uppercase', letterSpacing: 0.5 }}>{label}</p>
      <p style={{ fontFamily: 'Oswald, sans-serif', fontSize: 24, fontWeight: 900, color }} data-testid={testId}>{value}</p>
      {sub && <p style={{ fontSize: 11, color: '#555' }}>{sub}</p>}
    </div>
  );
}

export default function SportsKioskSalesPage() {
  const [[from, to], setRange] = useState(() => presetRange('7'));
  const [rolling, setRolling] = useState('since_paid');      // K8: opens on everything unsettled
  const [group, setGroup] = useState('device');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [denied, setDenied] = useState(false);

  const load = useCallback((silent = false) => {
    if (!silent) setLoading(true);
    return getSales(rolling ? { period: rolling, group } : { from, to, group })
      .then(d => { setData(d); setDenied(false); })
      .catch(e => { if (isAdminDenied(e)) setDenied(true); else if (!silent) toast.error(errMsg(e, 'Could not load sales')); })
      .finally(() => setLoading(false));
  }, [from, to, group, rolling]);
  useEffect(() => { load(); }, [load]);
  useAutoRefresh(() => load(true));

  const t = data?.totals;
  // T1: Booked and Played replace "Hours" once the server sends both; before that the page reads as it did.
  const bp = t?.booked_minutes != null && t?.played_minutes != null;
  // T1: "N vouchers · ₱X off" counts only the vouchers that took money off; free-time vouchers get their own tile.
  const discountVouchers = t ? (t.voucher_discount_count ?? t.vouchers) : 0;
  const bonusVouchers = t?.voucher_bonus_count || 0;
  const headerRight = (
    <button onClick={load} data-testid="kiosk-sales-refresh" title="Refresh"
      style={{ background: 'none', border: '1px solid #2A2A2A', borderRadius: 8, padding: '10px 12px', color: GREY, cursor: 'pointer' }}>
      <RefreshCw className="h-4 w-4" />
    </button>
  );

  const nameOf = (g) => {
    if (group === 'device') return { main: g.name, sub: [g.site_name, g.court_name].filter(Boolean).join(' · '), to: `/sports/super-admin/devices/${g.kiosk_id}` };
    if (group === 'site') return { main: g.site_name, sub: '' };
    return { main: g.court_name, sub: g.court_org_id ? '' : 'no tenant — public link only' };
  };

  return (
    <SuperAdminShell title="Sales" active="sales" headerRight={headerRight}>
      <main style={{ padding: '20px 16px' }} data-testid="sports-kiosk-sales-page">
        <p style={{ fontSize: 12, color: GREY, marginBottom: 14, maxWidth: 760, lineHeight: 1.5 }}>
          Summed from the rows each phone reported. <b style={{ color: '#ccc' }}>Collected at court</b> is the phone&apos;s own figure;
          <b style={{ color: '#ccc' }}> owner</b> and <b style={{ color: '#ccc' }}>court</b> are derived here with the phone&apos;s split rule (bearer included) — the phone&apos;s settlement is the record.
        </p>

        {denied ? <AdminDeniedNotice /> : (
          <>
            <div className="flex items-center justify-between gap-3 flex-wrap mb-5">
              <PeriodPicker from={from} to={to} onChange={(f, t2) => { setRolling(null); setRange([f, t2]); }} rolling={rolling} onRolling={setRolling} testPrefix="kiosk-sales" />
              <div className="flex gap-1" style={{ background: '#141414', border: '1px solid #1E1E1E', borderRadius: 10, padding: 3 }}>
                {GROUPS.map(([k, label]) => (
                  <button key={k} onClick={() => setGroup(k)} data-testid={`kiosk-sales-group-${k}`}
                    style={{ fontSize: 12, fontWeight: 700, padding: '6px 12px', borderRadius: 8, border: 'none', cursor: 'pointer',
                      background: group === k ? RED : 'transparent', color: group === k ? '#fff' : GREY }}>{label}</button>
                ))}
              </div>
            </div>

            {data?.period && (
              <p style={{ fontSize: 12, color: GREY, marginBottom: 12 }} data-testid="kiosk-sales-period">
                {rolling === 'since_paid' ? 'Every rental not yet settled by a phone or web collection, today included' : rolling === 'all' ? 'Everything the fleet has ever mirrored' : 'Selected range'}
                {' · '}{data.period.from} to {data.period.to}{data.period.capped ? ` · showing the latest ${data.period.max_rows || ''} rows only` : ''}
              </p>
            )}
            {t && (
              <div className={bp ? 'grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6' : 'grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-3 mb-6'}>
                <Tile label="Rentals" value={t.rentals} sub={`${t.stops} stopped · ${t.voids} not charged`} testId="kiosk-sales-rentals" />
                {bp ? (
                  <>
                    {/* T1: two honest facts, the kiosk's own words — never the money-derived "billed h". */}
                    <Tile label="Booked" value={hours(t.booked_minutes)} sub={fmtHrMin(t.booked_minutes)} testId="kiosk-sales-booked" />
                    <Tile label="Played" value={hours(t.played_minutes)} sub={fmtHrMin(t.played_minutes)} testId="kiosk-sales-played" />
                  </>
                ) : <Tile label="Hours" value={hours(t.minutes)} testId="kiosk-sales-hours" />}
                <Tile label="Collected at court" value={fmtCentavos(t.customers_paid_centavos)} testId="kiosk-sales-paid" />
                <Tile label="Due to HighMark" value={fmtCentavos(t.owner_share_centavos)} color={AMBER} sub="derived" testId="kiosk-sales-owner" />
                <Tile label="Retained by court" value={fmtCentavos(t.court_share_centavos)} color={t.court_share_centavos < 0 ? '#FF2D55' : GREEN} sub="derived" testId="kiosk-sales-court" />
                <Tile label="Comps · pro-rated · outages" value={`${t.comps} · ${t.pro_rated} · ${t.outages}`} testId="kiosk-sales-counts"
                  sub={t.underivable ? `${t.underivable} row(s) not derivable` : null} />
                <Tile label="Late stops" value={t.late_stops || 0} color={t.late_stops ? AMBER : '#fff'} testId="kiosk-sales-late"
                  sub={t.late_stops ? `${fmtCentavos(t.late_stop_forgone_centavos)} not charged` : 'problem stops at min 45–59'} />
              </div>
            )}
            {t && (discountVouchers > 0 || t.voucher_discount_centavos > 0) && (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6" data-testid="kiosk-sales-vouchers">
                <Tile label="Discount vouchers" value={discountVouchers} testId="kiosk-sales-voucher-count" />
                <Tile label="Discounts given" value={fmtCentavos(t.voucher_discount_centavos)} testId="kiosk-sales-voucher-discount" sub="off the full price" />
                <Tile label="Funded by HighMark" value={fmtCentavos(t.voucher_owner_funded_centavos)} color={AMBER} testId="kiosk-sales-voucher-owner" sub="already netted from Due to HighMark" />
                <Tile label="Funded by courts" value={fmtCentavos(t.voucher_court_funded_centavos)} color={GREEN} testId="kiosk-sales-voucher-court" sub="already netted from Retained by court" />
              </div>
            )}
            {/* T1: bonus-time vouchers take nothing off; they give machine time — so they stand in their
                own block, and a period with only free time never shows four ₱0.00 figures. */}
            {t && bonusVouchers > 0 && (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6" data-testid="kiosk-sales-vouchers-bonus">
                <Tile label="Free-time vouchers" value={bonusVouchers} sub={`${fmtHrMin(t.voucher_bonus_minutes || 0)} free`} testId="kiosk-sales-voucher-bonus" />
              </div>
            )}

            {loading && !data ? (
              <p style={{ color: GREY, textAlign: 'center', padding: 40 }}>Loading…</p>
            ) : data && data.groups.length === 0 ? (
              <div style={{ ...card, textAlign: 'center', padding: 50 }} data-testid="kiosk-sales-empty">
                <BarChart3 className="h-10 w-10 mx-auto mb-3" style={{ color: '#333' }} />
                <p style={{ color: GREY, fontSize: 14 }}>No finished rentals in this period.</p>
              </div>
            ) : data && (
              <div style={{ background: '#141414', border: '1px solid #1E1E1E', borderRadius: 12, overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: bp ? 1220 : 1140 }} data-testid="kiosk-sales-table">
                  <thead>
                    <tr style={{ borderBottom: '1px solid #1E1E1E' }}>
                      {[GROUPS.find(g => g[0] === group)[1].replace('By ', ''), 'Rentals', ...(bp ? ['Booked', 'Played'] : ['Hours']), 'Collected at court', 'Due to HighMark', 'Retained by court', 'Vouchers', 'Comps', 'Pro-rated', 'Late', 'Outages', 'Stops', 'Voids'].map((h, i) => (
                        <th key={h} style={{ ...labelStyle, textAlign: i === 0 ? 'left' : 'right', padding: '12px 14px', fontWeight: 700 }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {data.groups.map((g, i) => {
                      const n = nameOf(g);
                      const R = { padding: '12px 14px', fontSize: 13, textAlign: 'right' };
                      const gDisc = g.voucher_discount_count ?? g.vouchers;     // T1: only the vouchers that took money off
                      const gBonus = g.voucher_bonus_count || 0;
                      return (
                        <tr key={i} data-testid={`kiosk-sales-row-${i}`} style={{ borderBottom: '1px solid #1E1E1E' }}>
                          <td style={{ padding: '12px 14px' }}>
                            {n.to ? <Link to={n.to} style={{ fontFamily: 'Oswald, sans-serif', fontSize: 15, fontWeight: 700, color: '#fff', textDecoration: 'none' }}>{n.main}</Link>
                              : <span style={{ fontFamily: 'Oswald, sans-serif', fontSize: 15, fontWeight: 700 }}>{n.main}</span>}
                            {n.sub && <div style={{ fontSize: 11, color: GREY }}>{n.sub}</div>}
                          </td>
                          <td style={R}>{g.rentals}</td>
                          {/* T1: each group carries booked_minutes / played_minutes beside the totals. */}
                          {bp ? (
                            <>
                              <td style={R} title={fmtHrMin(g.booked_minutes)} data-testid={`kiosk-sales-row-${i}-booked`}>{hours(g.booked_minutes)}</td>
                              <td style={R} title={fmtHrMin(g.played_minutes)} data-testid={`kiosk-sales-row-${i}-played`}>{hours(g.played_minutes)}</td>
                            </>
                          ) : <td style={R}>{hours(g.minutes)}</td>}
                          <td style={{ ...R, fontWeight: 700 }}>{fmtCentavos(g.customers_paid_centavos)}</td>
                          <td style={{ ...R, color: AMBER }}>{fmtCentavos(g.owner_share_centavos)}</td>
                          <td style={{ ...R, color: g.court_share_centavos < 0 ? '#FF2D55' : GREEN }}>{fmtCentavos(g.court_share_centavos)}</td>
                          <td style={R} title={g.vouchers ? `HighMark ${fmtCentavos(g.voucher_owner_funded_centavos)} · court ${fmtCentavos(g.voucher_court_funded_centavos)}` : ''}>
                            {gDisc || gBonus ? <>
                              {gDisc ? <>{gDisc}<span style={{ color: '#555' }}> · {fmtCentavos(g.voucher_discount_centavos)}</span></> : null}
                              {gBonus ? <span style={{ color: '#555' }}>{gDisc ? ' · ' : ''}{gBonus} free-time voucher{gBonus === 1 ? '' : 's'} · {fmtHrMin(g.voucher_bonus_minutes || 0)} free</span> : null}
                            </> : '—'}
                          </td>
                          <td style={R}>{g.comps}{g.court_comps ? <span style={{ color: '#555' }}> ({g.court_comps} court)</span> : ''}</td>
                          <td style={R}>{g.pro_rated}</td>
                          <td style={{ ...R, color: g.late_stops ? AMBER : undefined }} title={g.late_stops ? `${fmtCentavos(g.late_stop_forgone_centavos)} not charged` : ''}>{g.late_stops || 0}</td>
                          <td style={{ ...R, color: g.outages ? '#FF2D55' : undefined }}>{g.outages}</td>
                          <td style={R}>{g.stops}</td>
                          <td style={R}>{g.voids}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
            {data && <p style={{ fontSize: 11, color: '#555', marginTop: 12, lineHeight: 1.5 }}>{data.derived_note}</p>}
          </>
        )}
      </main>
    </SuperAdminShell>
  );
}
